// @ts-ignore
/**
 * Centralized query keys for React Query
 * 
 * This file contains all the query keys used in the application.
 * Using these constants instead of inline strings helps with:
 * 1. Consistency across the application
 * 2. Type safety and autocompletion
 * 3. Easier refactoring
 * 4. Better organization of cache invalidation
 */

export const QUERY_KEYS = {
  // Team related keys
  TEAM: {
    root: 'team',
    detail: (teamId: string) => ['team', teamId],
    members: (teamId: string) => ['team', teamId, 'members'],
    games: (teamId: string) => ['team', teamId, 'games'],
    invitations: {
      user: ['team', 'invitations', 'user']
    },
    public: {
      detail: (teamId: string) => ['team', 'public', teamId],
      stats: (teamId: string) => ['team', 'public', teamId, 'stats'],
      members: (teamId: string) => ['team', 'public', teamId, 'members']
    },
    search: {
      all: (searchParams: any) => ['team', 'search', searchParams],
      my: (searchParams: any) => ['team', 'search', 'my', searchParams]
    },
    userTeams: ['user-teams']
  },

  TEAM_CONFIGURATIONS: {
    root: 'team-configurations',
    byId: (teamId: string) => ['team-configurations', teamId],
    byTeamId: (teamId: string) => ['team-configurations', 'by-team-id', teamId],
  },

  TEAM_SCHEDULE: {
    root: 'team-schedule',
    byTeamId: (teamId: string) => ['team-schedule', 'by-team-id', teamId],
    byId: (teamId: string, scheduleId: string) => ['team-schedule', 'by-id', teamId, scheduleId],
    checkins: (teamId: string, scheduleId: string) => ['team-schedule', 'checkins', teamId, scheduleId],
  },

  // Championship related keys
  CHAMPIONSHIP: {
    root: 'championship',
    byId: {
      public: (id: string) => ['championshipByIdPublic', id]
    },
    byUrl: {
      public: (url: string) => ['championshipByUrlPublic', url]
    },
    byStatus: (status: string) => ['championshipByStatus', status],
    byEntityId: (id: string) => ['getChampionshipByEntityId', id],
    participants: {
      byId: (id: string) => ['championshipParticipantsByIdPublic', id],
      byChampionshipAndEntityId: (championshipId: string, entityId: string) => 
        ['participantByChampionshipIdAndEntityId', championshipId, entityId]
    },
    registration: {
      byId: (registrationId: string) => ['registerChampionshipById', registrationId],
      byChampionshipId: (id: string) => ['registerByChampionshipId', id],
      update: ['updateRegisterUserChampionship']
    },
    tournament: (id: string) => ['championshipsTournament', id],
    public: ['championshipsPublic'],
    next: ['nextChampionships']
  },

  // Add other domains as needed
  USER: {
    root: 'user',
    preferences: ['user', 'preferences'],
    profile: (userId: string) => ['user', 'profile', userId],
    pix: (userId: string) => ['pix', userId],
    search: {
      byName: (name: string) => ['search_user', name],
      pagination: (searchParams: any) => ['getUsersSearchPagination', searchParams]
    },
    public: {
      byId: (id: string) => ['getUserByIdPublic', id],
      byNickname: (nickname: string) => ['getUserByNickNamePublic', nickname]
    },
    setup: (userId: string) => ['getSetupUserByIdPublic', userId],
    recommendations: ['getReComendations']
  },

  AUTH: {
    root: 'auth',
    session: ['auth', 'session'],
    user: ['auth', 'user']
  },

  NOTIFICATION: {
    root: 'notification',
    list: ['notification', 'list'],
    unread: ['notification', 'unread']
  },

  SUPPORT: {
    root: 'support',
    tickets: ['support', 'tickets'],
    ticket: (ticketId: string) => ['support', 'ticket', ticketId]
  },

  PAYMENT_HISTORY: {
    root: 'paymentHistory',
    byUser: (userId: string) => ['paymentHistory', userId]
  },

  GIFT: {
    root: 'gift',
    detail: (id: string) => ['gift', id],
    byReceiver: {
      detail: (receiverId: string) => ['gifts-by-receiver', receiverId],
      all: ['gifts-by-receiver']
    },
    bySender: {
      detail: (senderId: string) => ['gifts-by-sender', senderId],
      all: ['gifts-by-sender']
    }
  }
};
